/**
 * B403 403-low-note — the refused-bearer self-heal, as ONE seam.
 *
 * Measured over the real wire (2026-08-28): a FRESH guest holds every
 * read the storefront fires (catalog.list / slot.list / slot.availability
 * / slot.fit / funnel.mark all 200), so a walk's 403s are never a scope
 * refusal — they are a REMEMBERED bearer the SERVER refuses (guest-key
 * epoch or authorizer rotation) while the CLIENT deadline still trusts
 * it. guest.ts's ensure() consults only that deadline, so the dead bearer
 * was re-handed to every call and the whole page 403'd with no way out —
 * #37's stale-cart shape one layer down, healed the same way: retire the
 * refused credential, re-mint ONCE, replay the SAME call once; a second
 * refusal (a genuine authorization verdict) surfaces.
 *
 * The race guard: N in-flight calls can fail on the same stale bearer;
 * only the one whose bearer is STILL current clears it (the gate's shared
 * in-flight mint dedups the re-mint), so a heal never throws away a
 * sibling's fresh token.
 */
import { ApiError } from "./api.js";
import type { ApiClient, CallOptions } from "./api.js";
import type { GuestGate } from "./guest.js";

/** The server's own bearer verdicts — never a 404 (absence) and never a plain Error. */
export function isBearerRefusal(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

export function createBearerHealingClient(deps: {
  readonly api: ApiClient;
  readonly session: { token(): string | undefined; clearToken(): void };
  readonly gate: GuestGate;
}): ApiClient {
  return {
    call: async (name: string, fields: Record<string, unknown>, options?: CallOptions) => {
      const used = await deps.gate.ensure();
      try {
        return await deps.api.call(name, fields, options);
      } catch (error) {
        if (isBearerRefusal(error) === false) throw error;
        // The server's verdict outranks the client clock — but only on the
        // bearer THIS call used; a sibling's fresh mint is left standing.
        if (deps.session.token() === used) deps.session.clearToken();
        await deps.gate.ensure();
        return deps.api.call(name, fields, options);
      }
    },
  };
}
